import { useState, useRef } from "react";
import { Film, X, Loader2 } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoUploader({ currentVideo, onVideoChange }) {
  const { admin } = useAdminAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(currentVideo || "");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const uploadDirect = async (file) => {
    // Get presigned URL from backend
    const presignRes = await fetch(`${API_URL}/api/admin/upload/presign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${admin.token}`,
      },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
      }),
    });

    if (!presignRes.ok) {
      const err = await presignRes.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to get upload URL");
    }
    const { presignedUrl, publicUrl } = await presignRes.json();

    // Upload directly to S3 with progress tracking via XMLHttpRequest
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });

    return publicUrl;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    setProgress(0);

    try {
      const videoUrl = await uploadDirect(file);
      setPreview(videoUrl);
      onVideoChange(videoUrl);
    } catch (err) {
      console.error("Video upload failed:", err);
      setError(err.message || "Upload failed");
      setPreview(currentVideo || "");
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localUrl);
    }
  };

  const handleRemove = () => {
    setPreview("");
    setError("");
    onVideoChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div data-testid="video-uploader">
      {preview ? (
        <div className="relative rounded-lg overflow-hidden border border-[#2a2a2a] bg-[#1a1a1a]">
          <video
            src={preview}
            className="w-full h-40 object-cover"
            muted
            playsInline
            data-testid="video-preview"
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
              <Loader2 size={20} className="text-white animate-spin" />
              <div className="w-32 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#D8C08F] rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-white/70 text-xs font-sans">{progress}%</span>
            </div>
          )}
          {!uploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center hover:bg-black"
              data-testid="remove-video-button"
            >
              <X size={14} className="text-white" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full h-32 border-2 border-dashed border-[#2a2a2a] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#D8C08F]/50 transition-colors"
          data-testid="video-upload-trigger"
        >
          <Film size={20} className="text-[#555]" />
          <span className="text-[#555] text-xs font-sans">
            Click to upload video (MP4, WebM)
          </span>
        </button>
      )}
      {error && (
        <p className="text-red-400 text-xs font-sans mt-1.5" data-testid="video-upload-error">
          {error}
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        onChange={handleUpload}
        className="hidden"
        data-testid="video-file-input"
      />
    </div>
  );
}
